/**
 * Spread duties (+encounters, docs/design/encounters.md §5 C1/C4):
 *   spread - while a `counter: 'spread'` cast runs (Venom Rain circles, Triple Shot lines), every
 *            non-tank moves so its own circle/line would not catch an ally, staying in range of its
 *            target. Circles: >= radius + 2 yd from every ally. Lines from the caster: no ally inside
 *            my line, and I'm not inside theirs. Dodge cleans up whatever still lands.
 *   clear  - a tracking beam (or `counter: 'kite'` cast) on someone else: get to the far side of the
 *            beam's anchor so the beam never sweeps across me while its target kites.
 * Both are positional (not urgent): kicks and instant casts continue, cast-time spells wait.
 */
import type { AreaShape, DeepReadonly, Vec2, Zone } from '@keystone/sim';
import { angleDiff, angleOf, dist, fromAngle, sub } from '../geometry';
import type { ClassKit } from '../kits';
import type { BotMemory } from '../memory';
import { meleeRange, type DangerArea, type Perception, type RUnit } from '../perception';
import { castCounters, hold, moveTo, ringPoint, searchPoint, seen, type Duty } from './duty';

/** Extra clearance past a circle's radius between spread players. */
export const SPREAD_MARGIN = 2;
/** Extra lateral clearance past a line's half-width. */
const LINE_MARGIN = 1.5;
/** Units hugging the caster are hit by any nearby line regardless; they don't constrain spreading. */
const HUG_DIST = 5;
/** Farthest a spread step goes. */
const SPREAD_MAX_STEP = 18;
/** Ranged spread stays within this of its target. */
const RANGED_KEEP = 30;

/** Is `pt` inside a line of half-width `half` and length `len` from `origin` through `through`? */
function onLine(origin: Vec2, through: Vec2, pt: Vec2, half: number, len: number): boolean {
  const dir = fromAngle(angleOf(sub(through, origin)));
  const rel = sub(pt, origin);
  const fwd = rel.x * dir.x + rel.y * dir.y;
  if (fwd < 0 || fwd > len) return false;
  return Math.abs(rel.x * -dir.y + rel.y * dir.x) < half;
}

/** Spacing rule for one spread cast: is standing at `q` clear of every other ally? */
function spacingTest(p: Perception, caster: RUnit, shape: DeepReadonly<AreaShape> | undefined): ((q: Vec2) => boolean) | null {
  const others = p.party.filter((u) => u.id !== p.self.id);
  if (!shape || shape.type === 'circle' || shape.type === 'ring') {
    const r = shape?.type === 'circle' ? shape.radius : shape?.type === 'ring' ? shape.outer : 6;
    const need = r + SPREAD_MARGIN;
    return (q) => others.every((u) => dist(u.pos, q) >= need);
  }
  if (shape.type === 'line' || shape.type === 'rect') {
    const half = (shape.type === 'line' ? shape.width : shape.w) / 2 + LINE_MARGIN;
    const len = shape.type === 'line' ? shape.length : shape.h;
    const c = caster.pos;
    return (q) =>
      dist(q, c) >= HUG_DIST &&
      others.every((u) => dist(u.pos, c) < HUG_DIST || (!onLine(c, q, u.pos, half, len) && !onLine(c, u.pos, q, half, len)));
  }
  return null;
}

/** Range I want to keep from my current target while spreading. */
function keepRange(p: Perception, kit: ClassKit, target: RUnit | undefined): number {
  if (!target) return Infinity;
  return kit.ranged ? RANGED_KEEP : meleeRange(p.self, target) - 0.5;
}

export function spreadDuty(p: Perception, kit: ClassKit, mem: BotMemory, dangers: readonly DangerArea[]): Duty | null {
  if (kit.role === 'tank') return null;
  for (const c of castCounters(p, mem, 'spread')) {
    const cast = c.cast;
    const ab = cast ? p.view.content.enemyAbility(cast.abilityId) : undefined;
    if (!cast || !ab || ab.counter?.includes('kite') || dist(c.pos, p.self.pos) > 60) continue;
    const ok = spacingTest(p, c, ab.shape);
    if (!ok || !seen(p, mem, `spread:${c.id}:${cast.startedAt}`, cast.startedAt)) continue;
    if (ok(p.self.pos)) return { kind: 'spread', urgent: false, cmds: hold(p) };
    const target = p.self.targetId ? p.world.units[p.self.targetId] : undefined;
    const keep = keepRange(p, kit, target?.alive && target.faction !== p.self.faction ? target : c);
    const anchor = target?.alive && target.faction !== p.self.faction ? target : c;
    const dest = searchPoint(
      p,
      dangers,
      ok,
      (q) => dist(q, p.self.pos) + 3 * Math.max(0, dist(q, anchor.pos) - keep),
      SPREAD_MAX_STEP,
    );
    if (dest) return { kind: 'spread', urgent: false, cmds: moveTo(p, mem, dest) };
  }
  return null;
}

function reachOf(shape: DeepReadonly<AreaShape> | undefined): number {
  if (!shape) return 30;
  if (shape.type === 'line') return shape.length;
  if (shape.type === 'cone') return shape.range;
  if (shape.type === 'rect') return shape.h;
  if (shape.type === 'circle') return shape.radius;
  return 30;
}

/** Someone else's tracking beam: stand opposite its target around the anchor. */
export function beamClearDuty(p: Perception, kit: ClassKit, mem: BotMemory, dangers: readonly DangerArea[]): Duty | null {
  if (kit.role === 'tank') return null;
  let anchor: RUnit | undefined;
  let target: RUnit | undefined;
  let reach = 0;
  let key = '';
  let since = 0;
  for (const z of p.world.zones as readonly DeepReadonly<Zone>[]) {
    const m = z.motion;
    if (m?.spec.type !== 'aim' || !m.targetId || m.targetId === p.self.id) continue;
    anchor = p.world.units[m.anchorId ?? z.sourceId];
    target = p.world.units[m.targetId];
    reach = reachOf(z.area.shape);
    key = `clear:${z.id}`;
    since = z.createdAt;
    break;
  }
  if (!anchor) {
    const c = castCounters(p, mem, 'kite').find((e) => e.cast?.targetId && e.cast.targetId !== p.self.id);
    if (c?.cast?.targetId) {
      anchor = c;
      target = p.world.units[c.cast.targetId];
      reach = reachOf(p.view.content.enemyAbility(c.cast.abilityId)?.shape);
      key = `clear:${c.id}:${c.cast.startedAt}`;
      since = c.cast.startedAt;
    }
  }
  if (!anchor || !target?.alive) return null;
  const d = dist(p.self.pos, anchor.pos);
  if (d > reach + 2) return null;
  const toTarget = angleOf(sub(target.pos, anchor.pos));
  const mine = angleOf(sub(p.self.pos, anchor.pos));
  // Already well on the far side.
  if (Math.abs(angleDiff(mine, toTarget)) > (120 * Math.PI) / 180) return null;
  if (!seen(p, mem, key, since)) return null;
  const r = kit.ranged ? Math.max(Math.min(d, reach - 4), anchor.radius + 6) : Math.max(1, meleeRange(p.self, anchor) - 0.75);
  const dest = ringPoint(p, anchor.pos, toTarget + Math.PI, r, dangers);
  return dest ? { kind: 'clear', urgent: false, cmds: moveTo(p, mem, dest) } : null;
}
